import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { Button } from '@capsule/consumer-design-system';

import { push } from 'connected-react-router';
import styled, { css } from 'styled-components';
import InlineSVG from 'svg-inline-react';

import IconChatSvg from 'public/icon-chat.svg';
import PhoneSvg from 'public/Phone.svg';
import { AnalyticSources, UNIFIED } from 'src/app/analytics/analyticsEvents';
import useUnreadChatMessages from 'src/app/components/CapsuleChat/hooks/useUnreadChatMessages';
import { Card } from 'src/app/components/Elements/Card';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import { getCombinedOrderItemNames } from 'src/app/components/Models/Order/utils';
import { SIGNUP_SOURCES } from 'src/app/components/Pages/GetStarted/consts';
import { RescheduleOrderStepName } from 'src/app/components/Pages/Order/FailedOrderReschedule/types';
import { CardDeliveryDetailsItem, CardOrderSummaryItem } from 'src/app/components/Pages/Order/Tracking/Elements';
import { useDeliveryAddressById } from 'src/app/hooks/data/deliveryAddress';
import {
  useActivePendingMedicationList,
  useArchivedMedicationList,
  usePendingTransferList,
} from 'src/app/hooks/data/medication';
import { useOrderCount } from 'src/app/hooks/data/order';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { useTimeViewed } from 'src/app/hooks/useTimeViewed';
import { medications, rescheduleDeliveryWindows, rescheduleOrder } from 'src/app/router/paths';
import { getAccountSignupSource } from 'src/app/state/selectors';
import { openChat } from 'src/app/state/slices/chat';
import { useSelector, useDispatch } from 'src/app/state/store';
import type { HomeArchivedItem, HomeItem } from 'src/app/types';
import {
  HomeItemType,
  type CourierDeliveryMethod,
  type HomePrescriptionItem,
  type MailDeliveryMethod,
  type Order,
  type OrderTrackingCardName,
  type SegmentEvent,
} from 'src/app/types';
import { CTATypes, DeliveryMethodType, ORDER_STATUSES } from 'src/consts';

import { EXIT_TIMEOUT, INTERSECTION_THRESHOLD } from '../consts';
import { CardRescheduledDetailsItem } from '../Elements/TrackingCardItems/CardRescheduledDetailsItem';

import { CardTitle } from './CardTitle';
import { DownloadReceiptButton } from './DownloadReceiptButton';

const SummaryButtonContainer = styled('div')`
  ${({ theme }) => theme.mediaQueries.smallDown} {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: ${({ theme }) => theme.space.s4};
    background: linear-gradient(
      180deg,
      rgba(239, 245, 245, 0) 0%,
      rgba(239, 245, 245, 0.4) 20.83%,
      rgba(239, 245, 245, 0.8) 100%
    );
  }
`;

const SummaryCard = styled(Card)<{ hasBottomMargin?: boolean }>`
  padding: 20px 10px;
  background-color: white;
  margin-bottom: ${({ theme, hasBottomMargin }) => (hasBottomMargin ? theme.space.s5 : 0)};
  ${({ theme }) => theme.mediaQueries.smallDown} {
    margin-bottom: ${({ hasBottomMargin }) => (hasBottomMargin ? '7.2rem' : 0)};
  }
`;

const RescheduleContainer = styled(SummaryCard)`
  margin-top: ${({ theme }) => theme.space.s6};
`;

const ContactPhoneOrChatContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 6rem;
  justify-content: space-between;
  align-items: center;
`;

const StyledSVG = styled(InlineSVG)`
  svg {
    path {
      fill: ${props => props.theme.colors.capsuleGreen60};
    }
    width: 2rem;
  }
`;

interface OrderDetailCardsRendererProps {
  order: Order;
  dispatchSegmentTrack: SegmentEvent;
  shouldShowCourierMap: boolean;
}

export const OrderDetailCards: FC<OrderDetailCardsRendererProps> = ({
  order,
  dispatchSegmentTrack,
  shouldShowCourierMap,
}) => {
  const { data: deliveryAddress } = useDeliveryAddressById(order?.deliveryAddress?.id);

  const deliveryMethodKey = order?.deliveryMethod.key;
  const hours = order?.deliveryAddress?.hours;
  const closingTimeFlavorText = order?.deliveryAddress?.closingTimeFlavorText;
  const isCourierOrder = deliveryMethodKey === DeliveryMethodType.courier;
  const isMailOrder = deliveryMethodKey === DeliveryMethodType.mail;
  const isPickupStationOrder = deliveryMethodKey === DeliveryMethodType.pickupStation;
  const itemNames = order?.items?.map(orderItem => orderItem.name);
  const combinedItemNames = getCombinedOrderItemNames(itemNames);

  const { hasUnreadMessages } = useUnreadChatMessages();
  const dispatch = useDispatch();
  const dispatchGoToRescheduleDeliveryWindows = () =>
    dispatch(push(rescheduleDeliveryWindows.toPath({ orderId: order?.cartUUID })));
  const dispatchSelectRescheduleSegementEvent = useSegmentEvent('selectReschedule', { shouldTrackOnMount: false });
  const dispatchOpenChatSegementEvent = useSegmentEvent('openChat', { shouldTrackOnMount: false });
  const dispatchOpenRescheduleChat = () => {
    dispatchSelectRescheduleSegementEvent({ source: AnalyticSources.yourPrescriptions });
    dispatchOpenChatSegementEvent({
      source: AnalyticSources.rescheduleDeliveryOrderDetails,
      has_unread_message: hasUnreadMessages,
    });
    dispatch(
      openChat({
        prepopulatedMessage: `Hi Capsule, I need to reschedule my delivery for ${combinedItemNames}.`,
        source: AnalyticSources.rescheduleDeliveryOrderDetails,
      })
    );
  };

  const isOrderPostponed = order?.status === ORDER_STATUSES.postponed;
  const isOrderComplete = order?.status === ORDER_STATUSES.delivered;
  const orderRescheduleObject = order?.reschedule;
  const orderRescheduleType: CTATypes | undefined = order?.reschedule?.cta?.action?.type;

  // This handler is used by both the rescheduled card's reschedule button and
  // the change delivery time button. In both cases we want to toggle whether
  // to allow the user to self self service (dispatchGoToRescheduleDeliveryWindows)
  // or simply go to chat and chat in.
  const handleOnRescheduleClicked = () => {
    if (order?.cartUUID && orderRescheduleType === CTATypes.rescheduleOrderInvalidAddress) {
      dispatch(
        push(
          rescheduleOrder.toPath({
            orderId: order.cartUUID,
            stepName: RescheduleOrderStepName.Initial,
          })
        )
      );
      return;
    }

    if (orderRescheduleType === CTATypes.rescheduleOrder || orderRescheduleType === CTATypes.scheduleDelivery) {
      dispatchGoToRescheduleDeliveryWindows();
      return;
    }

    if (orderRescheduleType === CTATypes.openChat) {
      dispatchOpenRescheduleChat();
      return;
    }
  };

  const { data: orderCount } = useOrderCount();
  const isFirstOrder = useMemo(() => orderCount === 1, [orderCount]);

  // Check if the account has ever transferred a prescription
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));

  const { data: pendingTransfers } = usePendingTransferList(user ? parseInt(user.accountId) : -1, Boolean(user));
  const { data: archivedMedications } = useArchivedMedicationList(user ? parseInt(user.accountId) : -1, Boolean(user));
  const { data: activePendingMedications } = useActivePendingMedicationList(
    user ? parseInt(user.accountId) : -1,
    Boolean(user)
  );

  const activeMedications = activePendingMedications?.active?.flatMap((item: HomeItem) => item.items);

  const getTransfers = (items: (HomePrescriptionItem | HomeArchivedItem)[]) =>
    items.filter(item => item?.type === HomeItemType.transfer);

  const hasTransfers = useMemo(
    () =>
      getTransfers([...(archivedMedications ?? []), ...(activeMedications ?? [])]).length +
        (pendingTransfers ? pendingTransfers.length : 0) >
      0,
    [activeMedications, pendingTransfers, archivedMedications]
  );

  let mailDeliveryMethod = null;
  if (isMailOrder) {
    mailDeliveryMethod = order?.deliveryMethod as MailDeliveryMethod;
  }

  let courierDeliveryMethod = null;
  if (isCourierOrder) {
    courierDeliveryMethod = order?.deliveryMethod as CourierDeliveryMethod;
  }
  const courierPhone = courierDeliveryMethod?.courier?.phone;
  const courierName = courierDeliveryMethod?.courier?.name;

  const carrierName = mailDeliveryMethod?.carrier?.name;
  const accountSignupSource = useSelector(getAccountSignupSource);
  const shouldShowContinueButton: boolean =
    !hasTransfers && isFirstOrder && accountSignupSource === SIGNUP_SOURCES.DOCTOR_CHANNEL;

  const enterAction = (name: OrderTrackingCardName) => {
    dispatchSegmentTrack(UNIFIED.viewOrderTrackingCard, { card_name: name });
    if (name === 'transfer') {
      dispatchSegmentTrack(UNIFIED.viewTransferForm, { location: 'Order Tracking Transfer Form' });
    }

    if (shouldShowContinueButton) {
      dispatchSegmentTrack(UNIFIED.viewPostCheckoutContinue, {});
    }
  };
  const exitAction = (name: OrderTrackingCardName, timeViewed: number) =>
    dispatchSegmentTrack(UNIFIED.viewOrderTrackingCardTimeViewed, { card_name: name, time_viewed: timeViewed });

  const deliveryMethodSummaryBind = useTimeViewed(
    'delivery',
    enterAction,
    exitAction,
    EXIT_TIMEOUT,
    INTERSECTION_THRESHOLD
  );
  const orderSummaryBind = useTimeViewed(
    'order_summary',
    enterAction,
    exitAction,
    EXIT_TIMEOUT,
    INTERSECTION_THRESHOLD
  );

  const rescheduleOrderBind = useTimeViewed(
    'reschedule_order',
    enterAction,
    exitAction,
    EXIT_TIMEOUT,
    INTERSECTION_THRESHOLD
  );

  const redirectToLoggedInHome = () => {
    dispatch(push(medications.toPath()));
    dispatchSegmentTrack(UNIFIED.selectPostCheckoutCta, {});
  };

  useEffect(() => {
    if (isFirstOrder) {
      dispatchSegmentTrack(UNIFIED.viewTransferForm, { location: 'Order Tracking Page' });
    }
  }, [dispatchSegmentTrack, isFirstOrder]);

  return (
    <StandardPage alignment={'center'}>
      {shouldShowCourierMap && courierPhone && (
        <SummaryCard>
          <CardTitle title={courierName ?? 'Contact courier'}>
            <ContactPhoneOrChatContainer>
              <a data-testid="phone" href={`tel:${courierPhone}`}>
                <StyledSVG src={PhoneSvg} />
              </a>
              <a data-testid="chat" href={`sms:${courierPhone}`}>
                <StyledSVG src={IconChatSvg} />
              </a>
            </ContactPhoneOrChatContainer>
          </CardTitle>
        </SummaryCard>
      )}
      {isOrderPostponed && orderRescheduleObject && (
        <RescheduleContainer bind={rescheduleOrderBind}>
          <CardTitle title={orderRescheduleObject?.title} />
          <CardRescheduledDetailsItem
            description={orderRescheduleObject?.description}
            orderItems={orderRescheduleObject?.items}
            onRescheduleClick={handleOnRescheduleClicked}
            ctaType={orderRescheduleObject?.cta?.action?.type}
          />
        </RescheduleContainer>
      )}
      <SummaryCard bind={deliveryMethodSummaryBind}>
        <CardTitle title={isPickupStationOrder ? 'Pickup details' : 'Delivery details'} />
        {/* If the order is not postponed, product still allows the order to rescheduled in certain states which
        the bff sends back through the cta type */}
        {!isOrderPostponed &&
          (orderRescheduleType === CTATypes.openChat || orderRescheduleType === CTATypes.rescheduleOrder) && (
            <Button
              variant="text-md"
              css={css`
                padding: 0 ${({ theme }) => theme.space.s4};
              `}
              onClick={handleOnRescheduleClicked}
            >
              CHANGE DELIVERY TIME
            </Button>
          )}
        <CardDeliveryDetailsItem
          deliveryAddress={deliveryAddress}
          deliveryMethodKey={deliveryMethodKey}
          carrierName={carrierName}
          hours={hours}
          closingTimeFlavorText={closingTimeFlavorText}
        />
      </SummaryCard>
      <SummaryCard bind={orderSummaryBind} hasBottomMargin>
        <CardTitle title={'Order summary'}>
          {isOrderComplete && <DownloadReceiptButton orderId={order.id} dispatchSegmentTrack={dispatchSegmentTrack} />}
        </CardTitle>
        <CardOrderSummaryItem
          orderItems={order?.items}
          subtotal={order?.subtotal}
          tip={order?.tip}
          total={order?.total}
          tax={order?.tax}
        />
      </SummaryCard>
      {shouldShowContinueButton && (
        <SummaryButtonContainer>
          <Button isFullWidth onClick={redirectToLoggedInHome} variant="contained-secondary-lg">
            Continue
          </Button>
        </SummaryButtonContainer>
      )}
    </StandardPage>
  );
};
